import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { apiOrigin } from '../utils/surface';
import '../components/Layout/ShadowShell.css';
import '../components/Layout/ShadowMobile.css';
import './TenantPortal.css';

// Self-service portal for tenants. Mounted only on tenants.intrlock.io.
// Passwordless login: phone → SMS 6-digit code → 30-day session JWT
// stored in localStorage (sent as Bearer to /tenant-self/* endpoints).

const API = apiOrigin();
const TOKEN_KEY = 'intrlock_tenant_token';

interface PropertyLink {
  property_id: string;
  property_name: string;
  property_address: string;
  unit_id: string | null;
  unit_number: string | null;
  face_id_enrolled: boolean;
  face_access_enabled: boolean;
  // PM-controlled — false means the property manager hasn't enabled face
  // access for this property. UI greys out the card + hides enrollment.
  face_access_available?: boolean;
  // PM-controlled per-property toggles for delivery PINs and additional-tenant
  // additional-resident invitations. UI greys out + disables the form when false.
  deliveries_enabled?: boolean;
  additional_tenants_enabled?: boolean;
  // Per-property PIN (overrides legacy single people.access_pin).
  access_pin?: string | null;
  is_primary: boolean;
  // L2 per-property call routing prefs (overlays people.* defaults)
  accept_calls?: boolean;
  call_priority?: number;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
}

interface Me {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  access_pin: string | null;
  face_id_enrolled: boolean;
  face_access_enabled: boolean;
  unit_id: string | null;
  property_id: string;
  property_name: string;
  property_address: string;
  unit_number: string | null;
  properties?: PropertyLink[];
  // L2 phase 2c call routing prefs
  accept_calls?: boolean;
  call_priority?: number;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
}

interface CallRow {
  id: string;
  call_type: string;
  status: string;
  visitor_name: string | null;
  from_number: string | null;
  duration: number | null;
  door_unlocked: boolean;
  unlocked_door_names: string[] | null;
  created_at: string;
  snapshot_url: string | null;
}

async function api(path: string, init: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${API}${path}`, { ...init, headers });
  if (r.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
    throw new Error('unauthorized');
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
  return data;
}

// ───── Login screen ────────────────────────────────────────────────────────

// Country picker for the SMS-OTP login. Codes are stored as E.164 dial
// prefixes; the country code is concatenated with the local number before
// it hits the backend so PMs only ever see properly-formatted E.164.
const COUNTRY_CODES: { code: string; flag: string; name: string }[] = [
  { code: '+1',   flag: '🇺🇸', name: 'United States / Canada' },
  { code: '+52',  flag: '🇲🇽', name: 'Mexico' },
  { code: '+44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+33',  flag: '🇫🇷', name: 'France' },
  { code: '+49',  flag: '🇩🇪', name: 'Germany' },
  { code: '+34',  flag: '🇪🇸', name: 'Spain' },
  { code: '+39',  flag: '🇮🇹', name: 'Italy' },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands' },
  { code: '+46',  flag: '🇸🇪', name: 'Sweden' },
  { code: '+47',  flag: '🇳🇴', name: 'Norway' },
  { code: '+45',  flag: '🇩🇰', name: 'Denmark' },
  { code: '+41',  flag: '🇨🇭', name: 'Switzerland' },
  { code: '+43',  flag: '🇦🇹', name: 'Austria' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+30',  flag: '🇬🇷', name: 'Greece' },
  { code: '+48',  flag: '🇵🇱', name: 'Poland' },
  { code: '+420', flag: '🇨🇿', name: 'Czechia' },
  { code: '+36',  flag: '🇭🇺', name: 'Hungary' },
  { code: '+90',  flag: '🇹🇷', name: 'Turkey' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+27',  flag: '🇿🇦', name: 'South Africa' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+20',  flag: '🇪🇬', name: 'Egypt' },
  { code: '+91',  flag: '🇮🇳', name: 'India' },
  { code: '+92',  flag: '🇵🇰', name: 'Pakistan' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+86',  flag: '🇨🇳', name: 'China' },
  { code: '+81',  flag: '🇯🇵', name: 'Japan' },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea' },
  { code: '+852', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+886', flag: '🇹🇼', name: 'Taiwan' },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore' },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia' },
  { code: '+62',  flag: '🇮🇩', name: 'Indonesia' },
  { code: '+63',  flag: '🇵🇭', name: 'Philippines' },
  { code: '+66',  flag: '🇹🇭', name: 'Thailand' },
  { code: '+84',  flag: '🇻🇳', name: 'Vietnam' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia' },
  { code: '+64',  flag: '🇳🇿', name: 'New Zealand' },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil' },
  { code: '+54',  flag: '🇦🇷', name: 'Argentina' },
  { code: '+56',  flag: '🇨🇱', name: 'Chile' },
  { code: '+57',  flag: '🇨🇴', name: 'Colombia' },
  { code: '+51',  flag: '🇵🇪', name: 'Peru' },
];

const LoginScreen: React.FC<{ onLoggedIn: () => void }> = ({ onLoggedIn }) => {
  const [method, setMethod] = useState<'phone' | 'email'>('phone');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'identify' | 'code'>('identify');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // E.164 = country code + digits-only local number. The backend re-normalizes
  // but sending a clean value here gives sharper validation + error messages.
  const e164Phone = `${countryCode}${phone.replace(/\D/g, '')}`;
  const credentialDisplay = method === 'phone' ? e164Phone : email.trim();
  const identifyValid = method === 'phone'
    ? phone.replace(/\D/g, '').length >= 7
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const requestPayload = (): string => method === 'phone'
    ? JSON.stringify({ phone: e164Phone })
    : JSON.stringify({ email: email.trim() });

  const sendCode = async () => {
    setBusy(true); setErr('');
    try {
      await api('/tenant-auth/request-code', { method: 'POST', body: requestPayload() });
      setStep('code');
    } catch (e: any) { setErr(e.message || 'Could not send code'); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true); setErr('');
    try {
      const verifyBody = method === 'phone'
        ? JSON.stringify({ phone: e164Phone, code })
        : JSON.stringify({ email: email.trim(), code });
      const r = await api('/tenant-auth/verify-code', { method: 'POST', body: verifyBody });
      localStorage.setItem(TOKEN_KEY, r.token);
      onLoggedIn();
    } catch (e: any) { setErr(e.message || 'Invalid code'); }
    setBusy(false);
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '10px 12px',
    background: active ? '#fff' : '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderBottom: active ? '1px solid #fff' : '1px solid #e5e7eb',
    color: active ? '#111827' : '#6b7280',
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    fontSize: 14,
  });

  return (
    <div style={S.loginWrap}>
      <div style={S.brandBlock}>
        <div style={{ display: 'inline-block' }}>
          <img src="/intrlock-banner-dark.png" alt="Intrlock" style={S.brandLogoLarge} />
          <p style={S.brandTagline}>cloud-based intercom</p>
        </div>
      </div>
      <div style={S.loginCard}>
        <h2 style={S.h2}>Tenant Portal</h2>
        <p style={S.muted}>Sign in with the phone or email on file with your property manager.</p>

        {step === 'identify' && (
          <>
            <div style={{ display: 'flex', marginTop: 12, marginBottom: -1 }}>
              <button
                onClick={() => { setMethod('phone'); setErr(''); }}
                style={{ ...tabStyle(method === 'phone'), borderTopLeftRadius: 8, borderRight: 'none' }}
              >Phone</button>
              <button
                onClick={() => { setMethod('email'); setErr(''); }}
                style={{ ...tabStyle(method === 'email'), borderTopRightRadius: 8 }}
              >Email</button>
            </div>

            <div style={{
              border: '1px solid #e5e7eb', borderTop: 'none',
              borderRadius: '0 0 8px 8px', padding: 12, background: '#fff',
            }}>
              {method === 'phone' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{ ...S.input, flex: '0 0 110px', padding: '10px 6px' }}
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code + c.name} value={c.code}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel" inputMode="tel"
                    placeholder="555 555 5555"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={{ ...S.input, flex: 1 }}
                    autoFocus
                  />
                </div>
              ) : (
                <input
                  type="email" inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={S.input}
                  autoFocus
                />
              )}
              <p style={{ ...S.muted, fontSize: 12, marginTop: 8 }}>
                We&rsquo;ll text a 6-digit code to the phone on your tenant record.
              </p>
            </div>

            <button
              onClick={sendCode}
              disabled={busy || !identifyValid}
              style={{ ...S.primaryBtn, marginTop: 12 }}
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </>
        )}
        {step === 'code' && (
          <>
            <p style={S.muted}>
              We sent a 6-digit code to the phone on file for {credentialDisplay}. Enter it below.
            </p>
            <input
              type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...S.input, fontFamily: 'monospace', letterSpacing: 6, textAlign: 'center', fontSize: 22 }}
              autoFocus
            />
            <button onClick={verify} disabled={busy || code.length < 6} style={S.primaryBtn}>
              {busy ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <button onClick={() => { setStep('identify'); setCode(''); setErr(''); }} style={S.linkBtn}>
              ← change {method === 'phone' ? 'phone number' : 'email'}
            </button>
          </>
        )}
        {err && <div style={S.err}>{err}</div>}
      </div>
    </div>
  );
};

// ───── Dashboard ───────────────────────────────────────────────────────────

// Module-scope so the array reference stays stable across renders, letting us
// safely omit it from useEffect dependency arrays.
const TENANT_SECTION_IDS = [
  'sec-profile',
  'sec-properties',
  'sec-calls',
  'sec-pin',
  'sec-face',
  'sec-guests',
  'sec-deliveries',
  'sec-additional',
  'sec-mobile',
  'sec-history',
] as const;

const Dashboard: React.FC<{ onSignOut: () => void }> = ({ onSignOut }) => {
  const [me, setMe] = useState<Me | null>(null);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activationCode, setActivationCode] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [pinMsg, setPinMsg] = useState('');
  const todayISO = () => new Date().toISOString().slice(0, 16);
  const defaultEndISO = () => new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
  const [guestForm, setGuestForm] = useState<{
    property_id: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    starts_at: string;
    expires_at: string;
    device_ids: string[];
    pass_type: 'visitor' | 'delivery' | 'vendor';
    delivery_instructions: string;
    photo_required: boolean;
    geofence_required: boolean;
    consent_enabled: boolean;
    consent_title: string;
    consent_text: string;
    consent_acknowledgement: string;
  }>({
    property_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    starts_at: todayISO(),
    expires_at: defaultEndISO(),
    device_ids: [],
    pass_type: 'visitor',
    delivery_instructions: '',
    photo_required: false,
    geofence_required: false,
    consent_enabled: false,
    consent_title: 'Visitor consent',
    consent_text: '',
    consent_acknowledgement: 'I have read and agree to this consent form.',
  });
  const [guestDoors, setGuestDoors] = useState<{ id: string; name: string }[]>([]);
  const [guestDoorsLoading, setGuestDoorsLoading] = useState(false);
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  const [guestResult, setGuestResult] = useState<{ pin: string; name: string; starts_at: string; expires_at: string; doors: { id: string; name: string }[]; pass_url?: string } | null>(null);

  // Per-property tenant face opt-in (post-enrollment). Local map keyed by
  // property_id so toggles are optimistic.
  const [faceOptInSavingByProperty, setFaceOptInSavingByProperty] = useState<Record<string, boolean>>({});
  const [faceOptInMsgByProperty, setFaceOptInMsgByProperty] = useState<Record<string, string>>({});
  const toggleFaceOptIn = async (propertyId: string, next: boolean) => {
    setFaceOptInSavingByProperty((m) => ({ ...m, [propertyId]: true }));
    setFaceOptInMsgByProperty((m) => ({ ...m, [propertyId]: '' }));
    // Optimistic update on `me.properties`.
    setMe((prev) => prev && prev.properties ? {
      ...prev,
      properties: prev.properties.map((p) =>
        p.property_id === propertyId ? { ...p, face_access_enabled: next } : p,
      ),
    } : prev);
    try {
      await api('/tenant-self/property-face', {
        method: 'PUT',
        body: JSON.stringify({ property_id: propertyId, face_access_enabled: next }),
      });
    } catch (e: any) {
      setFaceOptInMsgByProperty((m) => ({ ...m, [propertyId]: e?.message || 'Save failed' }));
      // Roll back.
      setMe((prev) => prev && prev.properties ? {
        ...prev,
        properties: prev.properties.map((p) =>
          p.property_id === propertyId ? { ...p, face_access_enabled: !next } : p,
        ),
      } : prev);
    } finally {
      setFaceOptInSavingByProperty((m) => ({ ...m, [propertyId]: false }));
    }
  };

  // ── Deliveries (per-property) ──────────────────────────────────────────────
  const [deliveryFormByProperty, setDeliveryFormByProperty] = useState<Record<string, {
    carrier: string; arrival_at: string; notes: string;
  }>>({});
  const [deliverySubmitting, setDeliverySubmitting] = useState<Record<string, boolean>>({});
  const [deliveryMsg, setDeliveryMsg] = useState<Record<string, string>>({});
  const [deliveryResult, setDeliveryResult] = useState<Record<string, { pin: string; arrival_at: string; expires_at: string } | null>>({});

  const submitDelivery = async (propertyId: string) => {
    const f = deliveryFormByProperty[propertyId] || { carrier: '', arrival_at: '', notes: '' };
    if (!f.arrival_at) { setDeliveryMsg((m) => ({ ...m, [propertyId]: 'Arrival date/time is required' })); return; }
    setDeliveryMsg((m) => ({ ...m, [propertyId]: '' }));
    setDeliverySubmitting((m) => ({ ...m, [propertyId]: true }));
    try {
      const r = await api('/tenant-self/delivery-code', {
        method: 'POST',
        body: JSON.stringify({
          property_id: propertyId,
          arrival_at: new Date(f.arrival_at).toISOString(),
          carrier: f.carrier,
          notes: f.notes,
        }),
      });
      setDeliveryResult((m) => ({ ...m, [propertyId]: {
        pin: r.delivery.pin_code,
        arrival_at: r.delivery.arrival_at,
        expires_at: r.delivery.expires_at,
      }}));
      setDeliveryFormByProperty((m) => ({ ...m, [propertyId]: { carrier: '', arrival_at: '', notes: '' } }));
    } catch (e: any) {
      setDeliveryMsg((m) => ({ ...m, [propertyId]: e?.message || 'Failed to create delivery code' }));
    } finally {
      setDeliverySubmitting((m) => ({ ...m, [propertyId]: false }));
    }
  };

  // ── Additional tenants (per-property) ──────────────────────────────────────
  const [additionalFormByProperty, setAdditionalFormByProperty] = useState<Record<string, {
    first_name: string; last_name: string; phone: string; email: string; date_of_birth: string;
  }>>({});
  const [additionalSubmitting, setAdditionalSubmitting] = useState<Record<string, boolean>>({});
  const [additionalMsg, setAdditionalMsg] = useState<Record<string, string>>({});
  const [additionalResult, setAdditionalResult] = useState<Record<string, { name: string; activation_code: string; expires_at: string } | null>>({});

  const submitAdditional = async (propertyId: string) => {
    const f = additionalFormByProperty[propertyId] || { first_name: '', last_name: '', phone: '', email: '', date_of_birth: '' };
    if (!f.first_name.trim() || !f.last_name.trim()) {
      setAdditionalMsg((m) => ({ ...m, [propertyId]: 'First and last name are required' })); return;
    }
    if (!f.phone.trim()) {
      setAdditionalMsg((m) => ({ ...m, [propertyId]: 'Phone number is required' })); return;
    }
    setAdditionalMsg((m) => ({ ...m, [propertyId]: '' }));
    setAdditionalSubmitting((m) => ({ ...m, [propertyId]: true }));
    try {
      const r = await api('/tenant-self/additional-tenant', {
        method: 'POST',
        body: JSON.stringify({
          property_id: propertyId,
          first_name: f.first_name.trim(),
          last_name: f.last_name.trim(),
          phone: f.phone.trim(),
          email: f.email.trim() || undefined,
          date_of_birth: f.date_of_birth || undefined,
        }),
      });
      setAdditionalResult((m) => ({ ...m, [propertyId]: {
        name: `${r.additional_tenant.first_name} ${r.additional_tenant.last_name}`,
        activation_code: r.additional_tenant.activation_code,
        expires_at: r.additional_tenant.activation_expires_at,
      }}));
      setAdditionalFormByProperty((m) => ({ ...m, [propertyId]: { first_name: '', last_name: '', phone: '', email: '', date_of_birth: '' } }));
    } catch (e: any) {
      setAdditionalMsg((m) => ({ ...m, [propertyId]: e?.message || 'Failed to add tenant' }));
    } finally {
      setAdditionalSubmitting((m) => ({ ...m, [propertyId]: false }));
    }
  };

  // Profile edit-form state (Profile tab is now an editable form).
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  // Tab state — declared BEFORE any conditional early returns so the hook
  // order stays consistent on every render (React's rules of hooks).
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window === 'undefined') return TENANT_SECTION_IDS[0];
    const fromHash = window.location.hash.replace(/^#/, '');
    return (TENANT_SECTION_IDS as readonly string[]).includes(fromHash) ? fromHash : TENANT_SECTION_IDS[0];
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${activeTab}`) {
      window.history.replaceState(null, '', `#${activeTab}`);
    }
  }, [activeTab]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHashChange = () => {
      const id = window.location.hash.replace(/^#/, '');
      if ((TENANT_SECTION_IDS as readonly string[]).includes(id)) setActiveTab(id);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // L2 phase 2c — call routing prefs per property. Keyed by property_id so
  // a tenant on multiple properties can save independent settings for each.
  interface CallPrefs {
    accept_calls: boolean;
    call_priority: number;
    quiet_hours_start: string;
    quiet_hours_end: string;
  }
  const [prefsByProperty, setPrefsByProperty] = useState<Record<string, CallPrefs>>({});
  const [callPrefsMsg, setCallPrefsMsg] = useState<Record<string, string>>({});
  // Tracks which per-property cards are currently expanded. Key = "section:propertyId".
  // Defaulting all cards to collapsed keeps the page short — the user expands
  // only the property they want to edit.
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const toggleCard = (key: string) =>
    setExpandedCards((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  // Whole-section accordion state — sections start expanded so first-time
  // users see everything; users can collapse any section they don't need.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const toggleSection = (id: string) =>
    setCollapsedSections((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const isOpen = (id: string) => !collapsedSections.has(id);
  const SectionHeader: React.FC<{ id: string; title: string; summary?: string }> = ({ id, title, summary }) => {
    const open = isOpen(id);
    return (
      <button
        onClick={() => toggleSection(id)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          textAlign: 'left', marginBottom: open ? 8 : 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ ...S.h3, margin: 0 }}>{title}</span>
          {!open && summary && <span style={{ ...S.muted, fontSize: 12 }}>{summary}</span>}
        </div>
        <span style={{ color: '#71717a', fontSize: 22, lineHeight: 1, userSelect: 'none' }}>{open ? '−' : '+'}</span>
      </button>
    );
  };

  const updatePref = (propertyId: string, patch: Partial<CallPrefs>) => {
    setPrefsByProperty((cur) => ({
      ...cur,
      [propertyId]: { ...cur[propertyId], ...patch },
    }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const m = await api('/tenant-self/me');
      const ch = await api(`/events/calls?tenant_id=${encodeURIComponent(m.id)}`).catch(() => []);
      setMe(m);
      setCalls(Array.isArray(ch) ? ch : []);
      setPin(m?.access_pin || '');
      // Seed the per-property PIN editor from the loaded properties array.
      const propsForPin: any[] = (m?.properties && m.properties.length > 0)
        ? m.properties
        : (m?.property_id ? [{ property_id: m.property_id, access_pin: m.access_pin ?? null }] : []);
      const seededPins: Record<string, string> = {};
      for (const p of propsForPin) {
        seededPins[p.property_id] = p.access_pin ?? '';
      }
      setPinByProperty(seededPins);
      setProfileForm({
        first_name: m?.first_name || '',
        last_name: m?.last_name || '',
        email: m?.email || '',
      });
      // Seed guest-pin form with the tenant's primary/first property so the
      // selector + door list are populated by default.
      const firstProp = (m?.properties && m.properties[0]) || (m?.property_id ? { property_id: m.property_id } : null);
      if (firstProp) {
        setGuestForm((f) => ({ ...f, property_id: firstProp.property_id }));
        loadGuestDoors(firstProp.property_id);
      }
      // Seed per-property prefs from the response. Properties without
      // junction overrides inherit me.* (which the server already
      // overlayed into the property entry).
      const seeded: Record<string, CallPrefs> = {};
      const props: PropertyLink[] = (m?.properties && m.properties.length > 0)
        ? m.properties
        : (m?.property_id ? [{
            property_id: m.property_id,
            property_name: m.property_name,
            property_address: m.property_address,
            unit_id: m.unit_id,
            unit_number: m.unit_number,
            face_id_enrolled: m.face_id_enrolled,
            face_access_enabled: m.face_access_enabled,
            is_primary: true,
            accept_calls: m.accept_calls,
            call_priority: m.call_priority,
            quiet_hours_start: m.quiet_hours_start,
            quiet_hours_end: m.quiet_hours_end,
          }] : []);
      for (const p of props) {
        seeded[p.property_id] = {
          accept_calls: p.accept_calls !== false,
          call_priority: typeof p.call_priority === 'number' ? p.call_priority : 100,
          quiet_hours_start: p.quiet_hours_start || '',
          quiet_hours_end: p.quiet_hours_end || '',
        };
      }
      setPrefsByProperty(seeded);
    } catch {}
    setLoading(false);
  };

  const saveCallPrefs = async (propertyId: string) => {
    setCallPrefsMsg((m) => ({ ...m, [propertyId]: '' }));
    const prefs = prefsByProperty[propertyId];
    if (!prefs) return;
    try {
      await api('/tenant-self/property-prefs', {
        method: 'PUT',
        body: JSON.stringify({
          property_id: propertyId,
          accept_calls: prefs.accept_calls,
          call_priority: prefs.call_priority,
          quiet_hours_start: prefs.quiet_hours_start,
          quiet_hours_end: prefs.quiet_hours_end,
        }),
      });
      setCallPrefsMsg((m) => ({ ...m, [propertyId]: 'Saved' }));
      setTimeout(() => setCallPrefsMsg((m) => ({ ...m, [propertyId]: '' })), 3000);
    } catch (e: any) {
      setCallPrefsMsg((m) => ({ ...m, [propertyId]: e?.message || 'Save failed' }));
    }
  };
  useEffect(() => { load(); }, []);

  const savePin = async () => {
    setPinMsg('');
    const v = pin.replace(/\D/g, '');
    if (v.length > 0 && v.length < 4) { setPinMsg('PIN must be 4–8 digits'); return; }
    try {
      await api('/tenant-self/me', { method: 'PUT', body: JSON.stringify({ access_pin: v || null }) });
      setPinMsg(v ? 'PIN saved' : 'PIN removed');
      setTimeout(() => setPinMsg(''), 3000);
    } catch (e: any) { setPinMsg(e.message); }
  };

  // Per-property PIN state — local edits keyed by property_id, saved via
  // PUT /tenant-self/property-pin. Mirrors the per-property Call Settings UX.
  const [pinByProperty, setPinByProperty] = useState<Record<string, string>>({});
  const [pinMsgByProperty, setPinMsgByProperty] = useState<Record<string, string>>({});
  const [pinSavingByProperty, setPinSavingByProperty] = useState<Record<string, boolean>>({});
  // Visibility toggle — PINs are hidden by default; users click "Show" to
  // briefly reveal. Auto-hides after 8 seconds so a forgotten reveal in a
  // public space doesn't leak the code.
  const [pinRevealedByProperty, setPinRevealedByProperty] = useState<Record<string, boolean>>({});
  const savePropertyPin = async (propertyId: string) => {
    const v = (pinByProperty[propertyId] || '').replace(/\D/g, '');
    if (v.length > 0 && v.length < 4) {
      setPinMsgByProperty((m) => ({ ...m, [propertyId]: 'PIN must be 4–8 digits' }));
      return;
    }
    setPinSavingByProperty((m) => ({ ...m, [propertyId]: true }));
    setPinMsgByProperty((m) => ({ ...m, [propertyId]: '' }));
    try {
      const res = await api('/tenant-self/property-pin', {
        method: 'PUT',
        body: JSON.stringify({ property_id: propertyId, access_pin: v || null }),
      });
      setPinMsgByProperty((m) => ({ ...m, [propertyId]: v ? 'Saved' : 'PIN removed' }));
      // Update me.properties.access_pin locally so the summary refreshes.
      setMe((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next.properties = (prev.properties || []).map((p) =>
          p.property_id === propertyId ? { ...p, access_pin: res?.access_pin ?? (v || null) } : p,
        );
        if (next.property_id === propertyId) next.access_pin = res?.access_pin ?? (v || null);
        return next;
      });
      setTimeout(() => setPinMsgByProperty((m) => ({ ...m, [propertyId]: '' })), 3000);
    } catch (e: any) {
      setPinMsgByProperty((m) => ({ ...m, [propertyId]: e?.message || 'Save failed' }));
    } finally {
      setPinSavingByProperty((m) => ({ ...m, [propertyId]: false }));
    }
  };

  const regenerateActivation = async () => {
    try {
      const r = await api('/tenant-self/regenerate-activation', { method: 'POST' });
      setActivationCode(r.activation_code);
    } catch (e: any) { alert(e.message); }
  };

  const loadGuestDoors = async (propertyId: string) => {
    if (!propertyId) { setGuestDoors([]); return; }
    setGuestDoorsLoading(true);
    try {
      const r = await api(`/tenant-self/property-doors?property_id=${encodeURIComponent(propertyId)}`);
      const doors = Array.isArray(r?.doors) ? r.doors : [];
      setGuestDoors(doors);
      // Default-select all doors so a tenant who skips the step still gets
      // the same access they had before this UI shipped.
      setGuestForm((f) => ({ ...f, device_ids: doors.map((d: any) => String(d.id)) }));
    } catch {
      setGuestDoors([]);
    } finally {
      setGuestDoorsLoading(false);
    }
  };

  const createGuestPin = async () => {
    setGuestError(null);
    const fname = guestForm.first_name.trim();
    const lname = guestForm.last_name.trim();
    const phone = guestForm.phone.trim();
    if (!fname || !lname) { setGuestError('First and last name are required'); return; }
    if (!phone) { setGuestError('Phone number is required'); return; }
    if (!guestForm.property_id) { setGuestError('Please select a property'); return; }
    const startMs = Date.parse(guestForm.starts_at);
    const endMs   = Date.parse(guestForm.expires_at);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      setGuestError('End date must be after start date'); return;
    }
    setGuestSubmitting(true);
    try {
      const r = await api('/visitors/passes', {
        method: 'POST',
        body: JSON.stringify({
          property_id: guestForm.property_id,
          visitor_name: `${fname} ${lname}`.trim(),
          phone,
          email: guestForm.email.trim() || undefined,
          access_scope: guestForm.device_ids,
          valid_from: new Date(startMs).toISOString(),
          valid_until: new Date(endMs).toISOString(),
          pass_type: guestForm.pass_type,
          delivery_instructions: guestForm.delivery_instructions || undefined,
          photo_required: guestForm.photo_required,
          geofence_required: guestForm.geofence_required,
          second_factor_required: true,
          consent_form: guestForm.consent_enabled ? {
            enabled: true,
            title: guestForm.consent_title.trim(),
            text: guestForm.consent_text.trim(),
            acknowledgement: guestForm.consent_acknowledgement.trim(),
          } : undefined,
        }),
      });
      const selectedDoors = guestDoors.filter((d) => guestForm.device_ids.includes(String(d.id)));
      setGuestResult({
        pin: r.directory_code || '',
        name: r.visitor_name,
        starts_at: r.valid_from,
        expires_at: r.valid_until,
        doors: selectedDoors,
        pass_url: r.pass_url,
      });
      // Reset the name fields so the next pin starts fresh, but keep the
      // property + dates so a tenant minting several pins for one event
      // doesn't have to re-pick the same property each time.
      setGuestForm((f) => ({ ...f, first_name: '', last_name: '', phone: '', email: '' }));
    } catch (e: any) {
      setGuestError(e?.message || 'Failed to create visitor pass');
    } finally {
      setGuestSubmitting(false);
    }
  };

  // Per-property delete state — keyed by property_id. Holds the inline
  // confirm + result message so the user gets visible feedback instead of
  // relying on window.confirm() (which Safari + Chrome sometimes silently
  // ignore on touch / from non-gesture contexts).
  const [faceDeleteConfirm, setFaceDeleteConfirm] = useState<string | null>(null);
  const [faceDeleteBusy, setFaceDeleteBusy] = useState<string | null>(null);
  const [faceDeleteMsg, setFaceDeleteMsg] = useState<Record<string, string>>({});
  const removeFace = async (propertyId?: string) => {
    const key = propertyId || '_all';
    setFaceDeleteBusy(key);
    setFaceDeleteMsg((m) => ({ ...m, [key]: '' }));
    try {
      const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : '';
      const res: any = await api(`/tenant-self/face-enrollment${qs}`, { method: 'DELETE' });
      const rows = res?.rows_affected;
      setFaceDeleteMsg((m) => ({ ...m, [key]: typeof rows === 'number' ? `Removed (${rows} row${rows === 1 ? '' : 's'})` : 'Removed' }));
      setFaceDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setFaceDeleteMsg((m) => ({ ...m, [key]: `Failed: ${e?.message || e}` }));
    } finally {
      setFaceDeleteBusy(null);
    }
  };

  if (loading || !me) {
    return <div style={S.loginWrap}><div style={S.loginCard}>Loading…</div></div>;
  }

  const SECTIONS: { id: string; label: string; description: string; icon: React.ElementType; group: string }[] = [
    { id: 'sec-profile', label: 'Profile', description: 'Your contact information and tenant account', icon: Icons.UserRound, group: 'Account' },
    { id: 'sec-properties', label: 'Properties', description: 'Your properties, units, and building access', icon: Icons.Building2, group: 'Account' },
    { id: 'sec-calls', label: 'Call Settings', description: 'Control visitor call routing and quiet hours', icon: Icons.PhoneCall, group: 'Access' },
    { id: 'sec-pin', label: 'Access PIN', description: 'Manage your property access PINs', icon: Icons.KeyRound, group: 'Access' },
    { id: 'sec-face', label: 'Face Access', description: 'Enroll and manage secure face access', icon: Icons.ScanFace, group: 'Access' },
    { id: 'sec-guests', label: 'Guests', description: 'Create and manage visitor access', icon: Icons.UserRoundPlus, group: 'Visitors' },
    { id: 'sec-deliveries', label: 'Deliveries', description: 'Create time-limited delivery access', icon: Icons.Package, group: 'Visitors' },
    { id: 'sec-additional', label: 'Residents', description: 'Invite additional residents to your unit', icon: Icons.Users, group: 'Residents & App' },
    { id: 'sec-mobile', label: 'Mobile App', description: 'Activate and manage your Intrlock mobile app', icon: Icons.Smartphone, group: 'Residents & App' },
    { id: 'sec-history', label: 'History', description: 'Review recent visitor calls and access activity', icon: Icons.History, group: 'History & Reports' },
  ];
  const activeSection = SECTIONS.find((section) => section.id === activeTab) ?? SECTIONS[0];
  const initials = `${me.first_name?.[0] ?? ''}${me.last_name?.[0] ?? ''}`.toUpperCase() || 'T';
  const selectSection = (id: string) => {
    setActiveTab(id);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <div className="shadow-app-shell tenant-app-shell">
      {mobileNavOpen && (
        <button className="shadow-scrim" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside id="tenant-primary-navigation" className={`shadow-sidebar ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="shadow-brand">
          <img src="/intrlock-banner-white.png" alt="Intrlock" />
          <button onClick={() => setMobileNavOpen(false)} aria-label="Close navigation"><Icons.X /></button>
        </div>
        <nav className="shadow-nav" aria-label="Tenant portal navigation">
          {Array.from(new Set(SECTIONS.map((section) => section.group))).map((group) => (
            <div className="shadow-nav-group" key={group}>
              <div className="shadow-nav-label">{group}</div>
              {SECTIONS.filter((section) => section.group === group).map((section) => {
                const Icon = section.icon;
                return (
                  <div className="shadow-nav-row" key={section.id}>
                    <button
                      type="button"
                      className={`shadow-nav-item tenant-nav-button ${activeTab === section.id ? 'is-active' : ''}`}
                      aria-current={activeTab === section.id ? 'page' : undefined}
                      onClick={() => selectSection(section.id)}
                    >
                      <Icon /><span>{section.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="shadow-user">
          <span className="shadow-avatar mint">{initials}</span>
          <span className="shadow-user-copy">
            <strong>{`${me.first_name || ''} ${me.last_name || ''}`.trim() || me.email || 'Intrlock Tenant'}</strong>
            <small>Tenant</small>
          </span>
          <button aria-label="Sign out" title="Sign out" onClick={onSignOut}><Icons.LogOut /></button>
        </div>
      </aside>

      <div className="shadow-workspace">
        <header className="shadow-topbar">
          <button
            className="shadow-menu"
            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-controls="tenant-primary-navigation"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((open) => !open)}
          ><Icons.Menu /></button>
          <img className="shadow-mobile-logo" src="/intrlock-banner-light.png" alt="Intrlock" />
          <div className="shadow-page-heading">
            <span>Intrlock Tenant Portal / {activeSection.group}</span>
            <h1>{activeSection.label}</h1>
            <p>{activeSection.description}</p>
          </div>
          <div className="shadow-actions tenant-topbar-actions">
            <span className="tenant-property-chip"><Icons.Building2 />{me.property_name || 'My property'}</span>
            <button className="shadow-icon-button" aria-label="Sign out" title="Sign out" onClick={onSignOut}><Icons.LogOut /></button>
          </div>
        </header>

        <main className="shadow-content tenant-portal-content">

      {/* Profile — editable contact info */}
      {activeTab === 'sec-profile' && (
      <section id="sec-profile" style={S.section}>
        <h2 style={{ ...S.h2, marginBottom: 16 }}>Profile</h2>
        <p style={{ ...S.muted, marginBottom: 16 }}>Update your name and email. Phone changes are managed by your property manager.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <div>
            <label style={S.fieldLabel}>First name</label>
            <input
              type="text"
              value={profileForm.first_name}
              onChange={(e) => { setProfileForm((f) => ({ ...f, first_name: e.target.value })); setProfileMsg(null); setProfileErr(null); }}
              style={{ ...S.input, marginTop: 4 }}
            />
          </div>
          <div>
            <label style={S.fieldLabel}>Last name</label>
            <input
              type="text"
              value={profileForm.last_name}
              onChange={(e) => { setProfileForm((f) => ({ ...f, last_name: e.target.value })); setProfileMsg(null); setProfileErr(null); }}
              style={{ ...S.input, marginTop: 4 }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={S.fieldLabel}>Email</label>
          <input
            type="email"
            value={profileForm.email}
            onChange={(e) => { setProfileForm((f) => ({ ...f, email: e.target.value })); setProfileMsg(null); setProfileErr(null); }}
            style={{ ...S.input, marginTop: 4 }}
            placeholder="you@example.com"
          />
        </div>
        <div style={{ ...S.fieldRow, marginTop: 16, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
          <span style={S.fieldLabel}>Phone</span>
          <span>{me.phone}</span>
        </div>
        {profileErr && <div style={{ ...S.err, marginTop: 12 }}>{profileErr}</div>}
        {profileMsg && <div style={{ marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: '#dcfce7', color: '#166534', fontSize: 13 }}>{profileMsg}</div>}
        <button
          disabled={profileSaving}
          onClick={async () => {
            const fn = profileForm.first_name.trim();
            const ln = profileForm.last_name.trim();
            const em = profileForm.email.trim();
            if (!fn || !ln) { setProfileErr('First and last name are required'); return; }
            if (em && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setProfileErr('Email looks invalid'); return; }
            setProfileSaving(true);
            setProfileErr(null);
            setProfileMsg(null);
            try {
              const updated = await api('/tenant-self/me', {
                method: 'PUT',
                body: JSON.stringify({ first_name: fn, last_name: ln, email: em }),
              });
              setMe((prev) => prev ? { ...prev, first_name: updated.first_name ?? fn, last_name: updated.last_name ?? ln, email: updated.email ?? em } : prev);
              setProfileMsg('Saved');
              setTimeout(() => setProfileMsg(null), 3000);
            } catch (e: any) {
              setProfileErr(e?.message || 'Save failed');
            } finally {
              setProfileSaving(false);
            }
          }}
          style={{ ...S.primaryBtn, marginTop: 16, opacity: profileSaving ? 0.6 : 1 }}
        >
          {profileSaving ? 'Saving…' : 'Save changes'}
        </button>
      </section>
      )}

      {/* Properties — read-only list of all properties this tenant is at */}
      {activeTab === 'sec-properties' && (
      <section id="sec-properties" style={S.section}>
        <h2 style={S.h2}>Your Properties</h2>
        <p style={{ ...S.muted, marginBottom: 12 }}>
          Properties you're a tenant at. Contact your property manager to change any of these.
        </p>
        {(() => {
          const props: PropertyLink[] = (me.properties && me.properties.length > 0)
            ? me.properties
            : [{
                property_id: me.property_id,
                property_name: me.property_name,
                property_address: me.property_address,
                unit_id: me.unit_id,
                unit_number: me.unit_number,
                face_id_enrolled: me.face_id_enrolled,
                face_access_enabled: me.face_access_enabled,
                is_primary: true,
              } as PropertyLink];
          if (!props.length || !props[0]?.property_name) {
            return <p style={S.muted}>No properties are linked to your account yet.</p>;
          }
          return props.map((p) => (
            <div key={p.property_id} style={{ marginTop: 12, padding: 14, border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ ...S.h3, margin: 0 }}>{p.property_name}</h3>
                {p.is_primary && (
                  <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#166534' }}>Primary</span>
                )}
              </div>
              {p.property_address && (
                <div style={S.fieldRow}><span style={S.fieldLabel}>Address</span><span>{p.property_address}</span></div>
              )}
              {p.unit_number && (
                <div style={S.fieldRow}><span style={S.fieldLabel}>Unit</span><span>{p.unit_number}</span></div>
              )}
              <div style={S.fieldRow}>
                <span style={S.fieldLabel}>Face access</span>
                <span>{p.face_id_enrolled ? (p.face_access_enabled ? 'Enrolled · enabled' : 'Enrolled · disabled') : 'Not enrolled'}</span>
              </div>
            </div>
          ));
        })()}
      </section>
      )}

      {/* PIN code */}
      {activeTab === 'sec-pin' && (
      <section id="sec-pin" style={S.section}>
        <SectionHeader
          id="sec-pin"
          title="PIN Codes"
          summary={(() => {
            const props = (me.properties && me.properties.length ? me.properties : [{ property_id: me.property_id, access_pin: me.access_pin } as PropertyLink]);
            const setCount = props.filter((p) => (p.access_pin || pinByProperty[p.property_id] || '').replace(/\D/g, '').length >= 4).length;
            return `${setCount}/${props.length} ${props.length === 1 ? 'PIN set' : 'PINs set'}`;
          })()}
        />
        {isOpen('sec-pin') && <>
        <p style={S.muted}>
          Set a 4–8 digit PIN per property. Use it at the building kiosk or on the visitor website to unlock doors.
        </p>
        {(me.properties && me.properties.length > 0 ? me.properties : [{
          property_id: me.property_id,
          property_name: me.property_name,
          property_address: me.property_address,
          unit_id: me.unit_id,
          unit_number: me.unit_number,
          access_pin: me.access_pin ?? null,
          is_primary: true,
        } as PropertyLink]).map((p) => {
          const editedValue = pinByProperty[p.property_id] ?? '';
          const stored = (p.access_pin || '').replace(/\D/g, '');
          const saving = !!pinSavingByProperty[p.property_id];
          const msg = pinMsgByProperty[p.property_id] || '';
          const isError = /must|invalid|fail|already/i.test(msg);
          return (
            <div key={p.property_id} style={{
              marginTop: 12,
              padding: 14,
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: '#f9fafb',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontWeight: 600, color: '#111827', margin: 0 }}>
                    {p.property_name}
                    {p.unit_number ? <span style={{ ...S.muted, marginLeft: 8, fontWeight: 400 }}>Unit {p.unit_number}</span> : null}
                  </p>
                  {p.property_address && (
                    <p style={{ ...S.muted, fontSize: 12, marginTop: 2 }}>{p.property_address}</p>
                  )}
                </div>
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: stored ? '#dcfce7' : '#f3f4f6',
                  color: stored ? '#166534' : '#6b7280',
                  whiteSpace: 'nowrap',
                }}>
                  {stored ? 'PIN set' : 'No PIN'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={pinRevealedByProperty[p.property_id] ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={8}
                  placeholder="4–8 digits"
                  value={editedValue}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setPinByProperty((m) => ({ ...m, [p.property_id]: v }));
                    setPinMsgByProperty((m) => ({ ...m, [p.property_id]: '' }));
                  }}
                  style={{ ...S.input, flex: 1, fontFamily: 'monospace', letterSpacing: 4, marginTop: 0 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPinRevealedByProperty((m) => {
                      const willReveal = !m[p.property_id];
                      if (willReveal) {
                        // Auto-hide after 8 seconds so a forgotten reveal in a
                        // public space doesn't leak the code.
                        setTimeout(() => {
                          setPinRevealedByProperty((mm) => ({ ...mm, [p.property_id]: false }));
                        }, 8000);
                      }
                      return { ...m, [p.property_id]: willReveal };
                    });
                  }}
                  style={{ ...S.secondaryBtn, marginTop: 0, width: 'auto' }}
                >
                  {pinRevealedByProperty[p.property_id] ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => savePropertyPin(p.property_id)}
                  disabled={saving}
                  style={{ ...S.primaryBtn, marginTop: 0, width: 'auto', opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              {msg && (
                <div style={{ marginTop: 8, fontSize: 12, color: isError ? '#b91c1c' : '#166534' }}>{msg}</div>
              )}
            </div>
          );
        })}
        </>}
      </section>
      )}

      {/* Call Settings — L2 phase 2c (per property) */}
      {activeTab === 'sec-calls' && (
      <section id="sec-calls" style={S.section}>
        <SectionHeader
          id="sec-calls"
          title="Call Settings"
          summary={(() => {
            const props = me.properties && me.properties.length ? me.properties : [{ property_id: me.property_id } as PropertyLink];
            const onCount = props.filter((p) => (prefsByProperty[p.property_id]?.accept_calls ?? true)).length;
            return `${onCount}/${props.length} accepting calls`;
          })()}
        />
        {isOpen('sec-calls') && <>
        <p style={S.muted}>
          Control how visitor calls from each building&rsquo;s intercom reach you. If you
          live at more than one property, each address has its own settings.
        </p>

        {(me.properties && me.properties.length > 0 ? me.properties : [{
          property_id: me.property_id,
          property_name: me.property_name,
          property_address: me.property_address,
          unit_id: me.unit_id,
          unit_number: me.unit_number,
          face_id_enrolled: me.face_id_enrolled,
          face_access_enabled: me.face_access_enabled,
          is_primary: true,
        } as PropertyLink]).map((p) => {
          const prefs = prefsByProperty[p.property_id] || {
            accept_calls: true,
            call_priority: 100,
            quiet_hours_start: '',
            quiet_hours_end: '',
          };
          const msg = callPrefsMsg[p.property_id];
          const cardKey = `calls:${p.property_id}`;
          const isOpen = expandedCards.has(cardKey);
          // Compact one-line summary shown while the card is collapsed so the
          // user can see what's active at a glance.
          const priorityLabel =
            prefs.call_priority === 1 ? 'Ring 1st'
            : prefs.call_priority === 50 ? 'Ring 2nd'
            : prefs.call_priority === 200 ? 'Ring last'
            : 'Default order';
          const quiet = (prefs.quiet_hours_start && prefs.quiet_hours_end)
            ? `Quiet ${prefs.quiet_hours_start}–${prefs.quiet_hours_end}`
            : 'No quiet hours';
          const summary = `${prefs.accept_calls ? '✓ Accepting calls' : '✗ Calls off'} · ${priorityLabel} · ${quiet}`;
          return (
            <div
              key={p.property_id}
              style={{
                marginTop: 14,
                padding: 0,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#fbfbfd',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => toggleCard(cardKey)}
                aria-expanded={isOpen}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: 14,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    {p.property_name}
                    {p.unit_number ? <span style={{ ...S.muted, marginLeft: 8, fontWeight: 400 }}>Unit {p.unit_number}</span> : null}
                  </span>
                  <span style={{ color: '#71717a', fontSize: 18, lineHeight: 1, userSelect: 'none' }}>{isOpen ? '−' : '+'}</span>
                </div>
                {!isOpen && (
                  <div style={{ ...S.muted, fontSize: 12 }}>{summary}</div>
                )}
              </button>
              {isOpen && (
                <div style={{ padding: '0 14px 14px', borderTop: '1px solid #f3f4f6' }}>
              {p.property_address && (
                <div style={{ ...S.muted, marginTop: 12, marginBottom: 10 }}>{p.property_address}</div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  checked={prefs.accept_calls}
                  onChange={(e) => updatePref(p.property_id, { accept_calls: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>Accept calls at this property</span>
                  <span style={{ ...S.muted, display: 'block' }}>
                    Off = visitors hear &ldquo;not available&rdquo; for this unit; co-tenants on the same unit still ring.
                  </span>
                </span>
              </label>

              <div style={{ marginTop: 12 }}>
                <label style={{ ...S.muted, display: 'block', marginBottom: 4 }}>Ring order</label>
                <select
                  value={prefs.call_priority}
                  onChange={(e) => updatePref(p.property_id, { call_priority: parseInt(e.target.value, 10) })}
                  style={S.input}
                >
                  <option value={1}>1st — ring me first</option>
                  <option value={50}>2nd — ring me second</option>
                  <option value={100}>Default — same as co-tenants</option>
                  <option value={200}>Last — ring me last</option>
                </select>
                <div style={S.muted}>
                  Used when more than one tenant on this unit accepts calls. Lower = rings sooner.
                </div>
              </div>

              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ ...S.muted, display: 'block', marginBottom: 4 }}>Quiet hours start</label>
                  <input
                    type="time"
                    value={prefs.quiet_hours_start}
                    onChange={(e) => updatePref(p.property_id, { quiet_hours_start: e.target.value })}
                    style={S.input}
                  />
                </div>
                <div>
                  <label style={{ ...S.muted, display: 'block', marginBottom: 4 }}>Quiet hours end</label>
                  <input
                    type="time"
                    value={prefs.quiet_hours_end}
                    onChange={(e) => updatePref(p.property_id, { quiet_hours_end: e.target.value })}
                    style={S.input}
                  />
                </div>
              </div>
              <div style={S.muted}>
                During quiet hours your phone won&rsquo;t ring for this property. Leave both blank to
                disable. Overnight ranges (e.g. 22:00&ndash;07:00) are supported.
              </div>

              <button
                onClick={() => saveCallPrefs(p.property_id)}
                style={{ ...S.primaryBtn, marginTop: 12 }}
              >
                Save settings for {p.property_name}
              </button>
              {msg && (
                <div style={{ ...S.muted, marginTop: 8, color: msg === 'Saved' ? '#10b981' : '#ef4444' }}>
                  {msg}
                </div>
              )}
                </div>
              )}
            </div>
          );
        })}
        </>}
      </section>
      )}

      {/* Guest PIN */}
      {activeTab === 'sec-guests' && (() => {
        const allProps: PropertyLink[] = (me?.properties && me.properties.length > 0)
          ? me.properties
          : (me?.property_id ? [{
              property_id: me.property_id,
              property_name: me.property_name,
              property_address: me.property_address,
              unit_id: me.unit_id,
              unit_number: me.unit_number,
              is_primary: true,
            } as PropertyLink] : []);
        const togglDoor = (id: string) => {
          setGuestForm((f) => {
            const has = f.device_ids.includes(id);
            return { ...f, device_ids: has ? f.device_ids.filter((x) => x !== id) : [...f.device_ids, id] };
          });
        };
        return (
        <section id="sec-guests" style={S.section}>
          <SectionHeader id="sec-guests" title="Issue a Guest PIN" summary="One-time code for visitors" />
          {isOpen('sec-guests') && <>
          <p style={S.muted}>For deliveries, friends, or family. Select which doors and how long the code works.</p>

          <label style={{ ...S.muted, display: 'block', marginTop: 8 }}>Pass type</label>
          <select value={guestForm.pass_type} onChange={(e) => setGuestForm({ ...guestForm, pass_type: e.target.value as any })} style={S.input}>
            <option value="visitor">Visitor</option><option value="delivery">Delivery</option><option value="vendor">Temporary vendor</option>
          </select>

          <label style={{ ...S.muted, display: 'block', marginTop: 8 }}>Property</label>
          <select
            value={guestForm.property_id}
            onChange={(e) => {
              const pid = e.target.value;
              setGuestForm((f) => ({ ...f, property_id: pid, device_ids: [] }));
              loadGuestDoors(pid);
            }}
            style={S.input}
          >
            <option value="">— Select property —</option>
            {allProps.map((p) => (
              <option key={p.property_id} value={p.property_id}>
                {p.property_name || p.property_address || p.property_id}
              </option>
            ))}
          </select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            <input
              type="text" placeholder="First name *"
              value={guestForm.first_name}
              onChange={(e) => setGuestForm({ ...guestForm, first_name: e.target.value })}
              style={S.input}
            />
            <input
              type="text" placeholder="Last name *"
              value={guestForm.last_name}
              onChange={(e) => setGuestForm({ ...guestForm, last_name: e.target.value })}
              style={S.input}
            />
          </div>

          <input
            type="tel" placeholder="Phone number *"
            value={guestForm.phone}
            onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
            style={S.input}
          />
          {guestForm.pass_type === 'delivery' && <textarea placeholder="Delivery instructions" value={guestForm.delivery_instructions} onChange={(e) => setGuestForm({ ...guestForm, delivery_instructions: e.target.value })} style={{...S.input,minHeight:90}} />}

          <label style={{display:'flex',alignItems:'center',gap:8,marginTop:10,fontSize:14}}><input type="checkbox" checked={guestForm.geofence_required} onChange={(e)=>setGuestForm({...guestForm,geofence_required:e.target.checked})}/> Require the visitor to be at the property</label>
          <label style={{display:'flex',alignItems:'center',gap:8,marginTop:10,fontSize:14}}><input type="checkbox" checked={guestForm.photo_required} onChange={(e)=>setGuestForm({...guestForm,photo_required:e.target.checked})}/> Require a consented onboarding photo</label>
          <div style={{border:'1px solid #d1d5db',padding:12,marginTop:12}}>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:14,fontWeight:600}}><input type="checkbox" checked={guestForm.consent_enabled} onChange={(e)=>setGuestForm({...guestForm,consent_enabled:e.target.checked})}/> Require a visitor consent form</label>
            {guestForm.consent_enabled && <div style={{display:'grid',gap:8,marginTop:10}}>
              <input type="text" placeholder="Consent form title" value={guestForm.consent_title} onChange={(e)=>setGuestForm({...guestForm,consent_title:e.target.value})} style={S.input}/>
              <textarea placeholder="Consent disclosure" value={guestForm.consent_text} onChange={(e)=>setGuestForm({...guestForm,consent_text:e.target.value})} style={{...S.input,minHeight:120}}/>
              <input type="text" placeholder="Acknowledgement label" value={guestForm.consent_acknowledgement} onChange={(e)=>setGuestForm({...guestForm,consent_acknowledgement:e.target.value})} style={S.input}/>
            </div>}
          </div>
          <input
            type="email" placeholder="Email (optional)"
            value={guestForm.email}
            onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
            style={S.input}
          />

          <div className="tenant-pass-date-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            <div>
              <label style={{ ...S.muted, display: 'block' }}>Start date</label>
              <input
                type="datetime-local"
                value={guestForm.starts_at}
                onChange={(e) => setGuestForm({ ...guestForm, starts_at: e.target.value })}
                style={S.input}
              />
            </div>
            <div>
              <label style={{ ...S.muted, display: 'block' }}>End date</label>
              <input
                type="datetime-local"
                value={guestForm.expires_at}
                onChange={(e) => setGuestForm({ ...guestForm, expires_at: e.target.value })}
                style={S.input}
              />
            </div>
          </div>

          <label style={{ ...S.muted, display: 'block', marginTop: 12, fontWeight: 600 }}>Doors</label>
          {guestDoorsLoading ? (
            <p style={S.muted}>Loading doors…</p>
          ) : guestDoors.length === 0 ? (
            <p style={S.muted}>No doors available on this property.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {guestDoors.map((d) => {
                const id = String(d.id);
                const checked = guestForm.device_ids.includes(id);
                return (
                  <label key={id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 8,
                    border: `1px solid ${checked ? '#0f2440' : '#d4d4d8'}`,
                    background: checked ? '#f0f4ff' : '#fff',
                    cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglDoor(id)}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ fontSize: 14, color: '#111827' }}>{d.name || 'Unnamed door'}</span>
                  </label>
                );
              })}
            </div>
          )}

          {guestError && (
            <p style={{ ...S.muted, color: '#b91c1c', marginTop: 8 }}>{guestError}</p>
          )}

          <button
            onClick={createGuestPin}
            disabled={guestSubmitting}
            style={{ ...S.primaryBtn, marginTop: 12, opacity: guestSubmitting ? 0.6 : 1 }}
          >
            {guestSubmitting ? 'Creating…' : 'Create visitor pass'}
          </button>

          {guestResult && (
            <div style={S.successCard}>
              <div style={S.muted}>Directory code for {guestResult.name}</div>
              <div style={S.pinDisplay}>{guestResult.pin}</div>
              <div style={{fontWeight:700,marginTop:8}}>The visitor will choose their personal access PIN after text-message verification.</div>
              <div style={S.muted}>
                Valid {new Date(guestResult.starts_at).toLocaleString()} — {new Date(guestResult.expires_at).toLocaleString()}
              </div>
              {guestResult.doors.length > 0 && (
                <div style={{ ...S.muted, marginTop: 6 }}>
                  Doors: {guestResult.doors.map((d) => d.name).join(', ')}
                </div>
              )}
              <p style={{ ...S.muted, marginTop: 8 }}>
                Share this code with your guest. They enter it at the building kiosk or visitor website.
                {guestResult.pass_url && <><br/><a href={guestResult.pass_url} target="_blank" rel="noreferrer" style={{fontWeight:700,color:'#0f766e'}}>Open secure Visitor Pass</a><br/><button onClick={()=>navigator.clipboard.writeText(guestResult.pass_url!)} style={{...S.secondaryBtn,marginTop:8}}>Copy secure pass link</button></>}
              </p>
            </div>
          )}
          </>}
        </section>
      );
      })()}

      {/* Deliveries — per-property cards mimicking the PIN code UI. */}
      {activeTab === 'sec-deliveries' && (() => {
        const allProps: PropertyLink[] = (me.properties && me.properties.length > 0)
          ? me.properties
          : [{
              property_id: me.property_id,
              property_name: me.property_name,
              property_address: me.property_address,
              unit_id: me.unit_id,
              unit_number: me.unit_number,
              is_primary: true,
            } as PropertyLink];
        return (
        <section id="sec-deliveries" style={S.section}>
          <SectionHeader
            id="sec-deliveries"
            title="Deliveries"
            summary={`${allProps.filter((p) => p.deliveries_enabled).length}/${allProps.length} ${allProps.length === 1 ? 'property enabled' : 'properties enabled'}`}
          />
          {isOpen('sec-deliveries') && <>
          <p style={S.muted}>
            Generate a one-time PIN for a courier. Include a note for the driver (where to leave it, gate code, etc.).
          </p>
          {allProps.map((p) => {
            const enabled = p.deliveries_enabled === true;
            const form = deliveryFormByProperty[p.property_id] || { carrier: '', arrival_at: '', notes: '' };
            const submitting = !!deliverySubmitting[p.property_id];
            const msg = deliveryMsg[p.property_id] || '';
            const result = deliveryResult[p.property_id];
            return (
              <div key={p.property_id} style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: enabled ? '#f9fafb' : '#f3f4f6',
                opacity: enabled ? 1 : 0.7,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: '#111827', margin: 0 }}>
                      {p.property_name}
                      {p.unit_number ? <span style={{ ...S.muted, marginLeft: 8, fontWeight: 400 }}>Unit {p.unit_number}</span> : null}
                    </p>
                    {p.property_address && (
                      <p style={{ ...S.muted, fontSize: 12, marginTop: 2 }}>{p.property_address}</p>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                    padding: '2px 8px', borderRadius: 999,
                    background: enabled ? '#dcfce7' : '#f3f4f6',
                    color: enabled ? '#166534' : '#6b7280',
                    whiteSpace: 'nowrap',
                  }}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {!enabled ? (
                  <p style={{ ...S.muted, fontSize: 13, margin: 0 }}>
                    Your property manager has not enabled delivery codes for this property.
                  </p>
                ) : (
                  <>
                    <label style={{ ...S.muted, display: 'block', marginTop: 4 }}>Expected arrival</label>
                    <input
                      type="datetime-local"
                      value={form.arrival_at}
                      onChange={(e) => setDeliveryFormByProperty((m) => ({ ...m, [p.property_id]: { ...form, arrival_at: e.target.value } }))}
                      style={{ ...S.input, marginTop: 0 }}
                    />
                    <input
                      type="text" placeholder="Carrier (UPS, FedEx, Amazon…)"
                      value={form.carrier}
                      onChange={(e) => setDeliveryFormByProperty((m) => ({ ...m, [p.property_id]: { ...form, carrier: e.target.value } }))}
                      style={S.input}
                    />
                    <label style={{ ...S.muted, display: 'block', marginTop: 8 }}>Message for driver</label>
                    <textarea
                      placeholder="Where to leave it, gate code, etc."
                      value={form.notes}
                      onChange={(e) => setDeliveryFormByProperty((m) => ({ ...m, [p.property_id]: { ...form, notes: e.target.value } }))}
                      rows={3}
                      style={{ ...S.input, marginTop: 0, resize: 'vertical', minHeight: 72 }}
                    />
                    <button
                      onClick={() => submitDelivery(p.property_id)}
                      disabled={submitting}
                      style={{ ...S.primaryBtn, marginTop: 10, opacity: submitting ? 0.6 : 1 }}
                    >
                      {submitting ? 'Generating…' : 'Generate access code'}
                    </button>
                    {msg && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{msg}</div>
                    )}
                    {result && (
                      <div style={S.successCard}>
                        <div style={S.muted}>Delivery access code</div>
                        <div style={S.pinDisplay}>{result.pin}</div>
                        <div style={S.muted}>
                          Arrival {new Date(result.arrival_at).toLocaleString()} · valid until {new Date(result.expires_at).toLocaleString()}
                        </div>
                        <p style={{ ...S.muted, marginTop: 8 }}>
                          Share this code with the driver. They enter it at the building kiosk or visitor website.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          </>}
        </section>
        );
      })()}

      {/* Additional Tenants — per-property cards. */}
      {activeTab === 'sec-additional' && (() => {
        const allProps: PropertyLink[] = (me.properties && me.properties.length > 0)
          ? me.properties
          : [{
              property_id: me.property_id,
              property_name: me.property_name,
              property_address: me.property_address,
              unit_id: me.unit_id,
              unit_number: me.unit_number,
              is_primary: true,
            } as PropertyLink];
        return (
        <section id="sec-additional" style={S.section}>
          <SectionHeader
            id="sec-additional"
            title="Additional Tenants"
            summary={`${allProps.filter((p) => p.additional_tenants_enabled).length}/${allProps.length} ${allProps.length === 1 ? 'property enabled' : 'properties enabled'}`}
          />
          {isOpen('sec-additional') && <>
          <p style={S.muted}>
            Invite a roommate, partner, or family member to share your unit. They'll receive an activation code to install the Intrlock mobile app.
          </p>
          {allProps.map((p) => {
            const enabled = p.additional_tenants_enabled === true;
            const form = additionalFormByProperty[p.property_id] || { first_name: '', last_name: '', phone: '', email: '', date_of_birth: '' };
            const submitting = !!additionalSubmitting[p.property_id];
            const msg = additionalMsg[p.property_id] || '';
            const result = additionalResult[p.property_id];
            const setField = (k: string, v: string) => setAdditionalFormByProperty((m) => ({ ...m, [p.property_id]: { ...form, [k]: v } }));
            return (
              <div key={p.property_id} style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: enabled ? '#f9fafb' : '#f3f4f6',
                opacity: enabled ? 1 : 0.7,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: '#111827', margin: 0 }}>
                      {p.property_name}
                      {p.unit_number ? <span style={{ ...S.muted, marginLeft: 8, fontWeight: 400 }}>Unit {p.unit_number}</span> : null}
                    </p>
                    {p.property_address && (
                      <p style={{ ...S.muted, fontSize: 12, marginTop: 2 }}>{p.property_address}</p>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                    padding: '2px 8px', borderRadius: 999,
                    background: enabled ? '#dcfce7' : '#f3f4f6',
                    color: enabled ? '#166534' : '#6b7280',
                    whiteSpace: 'nowrap',
                  }}>
                    {enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {!enabled ? (
                  <p style={{ ...S.muted, fontSize: 13, margin: 0 }}>
                    Your property manager has not enabled additional tenants for this property.
                  </p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input type="text" placeholder="First name *" value={form.first_name}
                        onChange={(e) => setField('first_name', e.target.value)} style={S.input} />
                      <input type="text" placeholder="Last name *" value={form.last_name}
                        onChange={(e) => setField('last_name', e.target.value)} style={S.input} />
                    </div>
                    <input type="tel" placeholder="Phone number *" value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)} style={S.input} />
                    <input type="email" placeholder="Email (optional)" value={form.email}
                      onChange={(e) => setField('email', e.target.value)} style={S.input} />
                    <label style={{ ...S.muted, display: 'block', marginTop: 8 }}>Date of birth (optional, marks minors)</label>
                    <input type="date" value={form.date_of_birth}
                      onChange={(e) => setField('date_of_birth', e.target.value)} style={{ ...S.input, marginTop: 0 }} />
                    <button
                      onClick={() => submitAdditional(p.property_id)}
                      disabled={submitting}
                      style={{ ...S.primaryBtn, marginTop: 10, opacity: submitting ? 0.6 : 1 }}
                    >
                      {submitting ? 'Adding…' : 'Add tenant'}
                    </button>
                    {msg && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{msg}</div>}
                    {result && (
                      <div style={S.successCard}>
                        <div style={S.muted}>Activation code for {result.name}</div>
                        <div style={S.pinDisplay}>{result.activation_code}</div>
                        <div style={S.muted}>
                          Expires {new Date(result.expires_at).toLocaleString()}
                        </div>
                        <p style={{ ...S.muted, marginTop: 8 }}>
                          Share this code with them. They'll enter it in the Intrlock mobile app to activate their access.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          </>}
        </section>
        );
      })()}

      {/* Mobile app */}
      {activeTab === 'sec-mobile' && (
      <section id="sec-mobile" style={S.section}>
        <SectionHeader id="sec-mobile" title="Mobile App" summary="Re-link the Intrlock app" />
        {isOpen('sec-mobile') && <>
        <p style={S.muted}>
          Receive video and voice intercom calls on your phone. Lost your activation code?
          Generate a new one to re-link the Intrlock app.
        </p>
        <button onClick={regenerateActivation} style={S.secondaryBtn}>
          Send me a new activation code
        </button>
        {activationCode && (
          <div style={S.successCard}>
            <div style={S.muted}>New activation code</div>
            <div style={S.pinDisplay}>{activationCode}</div>
            <p style={{ ...S.muted, marginTop: 8 }}>
              We also texted this to your phone. Enter it in the Intrlock mobile app.
            </p>
          </div>
        )}
        </>}
      </section>

      )}

      {/* Face enrollment — one enrollment per person, applied to every
          property the tenant lives at (and any added later). */}
      {activeTab === 'sec-face' && (() => {
        const allProps: PropertyLink[] = (me.properties && me.properties.length > 0
          ? me.properties
          : [{
              property_id: me.property_id,
              property_name: me.property_name,
              property_address: me.property_address,
              unit_id: me.unit_id,
              unit_number: me.unit_number,
              face_id_enrolled: me.face_id_enrolled,
              face_access_enabled: me.face_access_enabled,
              face_access_available: !!(me as any).face_access_available,
              is_primary: true,
            } as PropertyLink]);
        const enabledProps = allProps.filter((p) => p.face_access_available !== false);
        const enrolledProps = allProps.filter((p) => p.face_id_enrolled);
        const isEnrolled = enrolledProps.length > 0;
        const noPropertiesEnableFace = enabledProps.length === 0;
        return (
        <section id="sec-face" style={S.section}>
          <SectionHeader
            id="sec-face"
            title="Face Recognition"
            summary={
              noPropertiesEnableFace
                ? 'Not enabled by any property'
                : isEnrolled
                  ? `Enrolled (${enrolledProps.length}/${enabledProps.length} ${enabledProps.length === 1 ? 'property' : 'properties'})`
                  : 'Not enrolled'
            }
          />
          {isOpen('sec-face') && <>
          <p style={S.muted}>
            Enroll your face once and it works at every property where the property manager has turned face access on. New properties added to your account are auto-applied.
          </p>

          {/* ── Enrollment card (top) ────────────────────────────────────── */}
          {noPropertiesEnableFace ? (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 10, background: '#f9fafb', color: '#6b7280', fontSize: 13 }}>
              The property manager has not enabled this feature for any of your properties.
            </div>
          ) : isEnrolled ? (
            <>
              <div style={{ marginTop: 12, padding: 12, border: '1px solid #d1fae5', borderRadius: 10, background: '#ecfdf5' }}>
                <p style={{ color: '#065f46', fontWeight: 600, fontSize: 14, margin: 0 }}>✓ Face enrolled</p>
                <p style={{ ...S.muted, color: '#065f46', fontSize: 12, marginTop: 4 }}>
                  Use the per-property toggles below to choose where it's active.
                </p>
              </div>
              {faceDeleteConfirm === '_all' ? (
                <div style={{ marginTop: 12, padding: 12, border: '1px solid #fca5a5', borderRadius: 8, background: '#fef2f2' }}>
                  <p style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 10 }}>
                    Remove your face enrollment from all properties? You can re-enroll any time.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setFaceDeleteConfirm(null)}
                      disabled={faceDeleteBusy === '_all'}
                      style={{ ...S.secondaryBtn, marginTop: 0, flex: 1 }}
                    >Cancel</button>
                    <button
                      onClick={() => removeFace(undefined)}
                      disabled={faceDeleteBusy === '_all'}
                      style={{ ...S.primaryBtn, marginTop: 0, flex: 1, backgroundColor: '#b91c1c' }}
                    >{faceDeleteBusy === '_all' ? 'Removing…' : 'Yes, remove'}</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setFaceDeleteConfirm('_all'); setFaceDeleteMsg((m) => ({ ...m, _all: '' })); }} style={S.dangerBtn}>
                  Delete face enrollment
                </button>
              )}
              {faceDeleteMsg['_all'] && (
                <p style={{ fontSize: 12, marginTop: 8, color: faceDeleteMsg['_all'].startsWith('Failed') ? '#b91c1c' : '#166534' }}>
                  {faceDeleteMsg['_all']}
                </p>
              )}
            </>
          ) : (
            <FaceEnrollPanel me={me} properties={enabledProps} onEnrolled={load} />
          )}

          {/* ── Per-property list (below the enrollment card) ────────────── */}
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: '20px 0 8px' }}>Your properties</h4>
          {allProps.map((p) => {
            const pmEnabled = p.face_access_available !== false;
            const tenantOptIn = p.face_access_enabled !== false;
            const saving = !!faceOptInSavingByProperty[p.property_id];
            const msg = faceOptInMsgByProperty[p.property_id] || '';
            const effective = pmEnabled && tenantOptIn && isEnrolled;
            return (
              <div key={p.property_id} style={{
                marginTop: 8,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: pmEnabled ? '#f9fafb' : '#f3f4f6',
                opacity: pmEnabled ? 1 : 0.7,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: '#111827', margin: 0 }}>
                      {p.property_name}
                      {p.unit_number ? <span style={{ ...S.muted, marginLeft: 8, fontWeight: 400 }}>Unit {p.unit_number}</span> : null}
                    </p>
                    {p.property_address && (
                      <p style={{ ...S.muted, fontSize: 12, marginTop: 2 }}>{p.property_address}</p>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                    padding: '2px 8px', borderRadius: 999,
                    background: effective ? '#dcfce7' : pmEnabled ? '#fef3c7' : '#f3f4f6',
                    color: effective ? '#166534' : pmEnabled ? '#92400e' : '#6b7280',
                    whiteSpace: 'nowrap',
                  }}>
                    {effective ? 'Active' : pmEnabled ? (isEnrolled ? 'Off' : 'Not enrolled') : 'Not enabled'}
                  </span>
                </div>
                {!pmEnabled ? (
                  <p style={{ ...S.muted, fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                    Your property manager has not enabled face access here.
                  </p>
                ) : !isEnrolled ? (
                  <p style={{ ...S.muted, fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                    Enroll your face above to opt in. You can disable it at specific properties any time.
                  </p>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, cursor: saving ? 'wait' : 'pointer' }}>
                    <input
                      type="checkbox"
                      disabled={saving}
                      checked={tenantOptIn}
                      onChange={(e) => toggleFaceOptIn(p.property_id, e.target.checked)}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ fontSize: 13, color: '#111827' }}>
                      Use face access at this property
                    </span>
                  </label>
                )}
                {msg && (
                  <p style={{ fontSize: 12, marginTop: 6, color: '#b91c1c' }}>{msg}</p>
                )}
              </div>
            );
          })}
          </>}
        </section>
        );
      })()}

      {/* Call history */}
      {activeTab === 'sec-history' && (
      <section id="sec-history" style={S.section}>
        <SectionHeader
          id="sec-history"
          title="Recent Calls"
          summary={calls.length === 0 ? 'No recent calls' : `${calls.length} recent`}
        />
        {isOpen('sec-history') && <>
        {calls.length === 0 ? (
          <p style={S.muted}>No recent calls.</p>
        ) : (
          <div>
            {calls.slice(0, 25).map(c => (
              <div key={c.id} style={S.callRow}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>
                    {c.visitor_name || c.from_number || (c.call_type === 'video' ? 'Visitor (Video)' : 'Visitor')}
                  </div>
                  <div style={{ ...S.muted, fontSize: 12 }}>
                    {new Date(c.created_at).toLocaleString()} · {c.call_type} · {c.status}
                    {c.duration ? ` · ${Math.round(c.duration)}s` : ''}
                    {c.door_unlocked && c.unlocked_door_names?.length ? ` · unlocked ${c.unlocked_door_names.join(', ')}` : ''}
                  </div>
                </div>
                {c.snapshot_url && (
                  <img src={c.snapshot_url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                )}
              </div>
            ))}
          </div>
        )}
        </>}
      </section>
      )}

        <footer className="tenant-portal-footer">
          Intrlock Tenant Portal · <a href="https://intrlock.io/support" className="text-brand-600 hover:text-brand-700">Support</a>
        </footer>
        </main>
      </div>
    </div>
  );
};

// ───── Root ────────────────────────────────────────────────────────────────

export const TenantPortal: React.FC = () => {
  const [signedIn, setSignedIn] = useState(() => !!localStorage.getItem(TOKEN_KEY));
  return signedIn
    ? <Dashboard onSignOut={() => { localStorage.removeItem(TOKEN_KEY); setSignedIn(false); }} />
    : <LoginScreen onLoggedIn={() => setSignedIn(true)} />;
};

// ───── Face enrollment (camera capture) ────────────────────────────────────
//
// Captures 3 still frames from the user's front camera, base64-encodes them,
// and posts to /face/enroll. Same endpoint the iOS app uses — the lambda
// handles multi-image quality scoring and writes a face_enrollments row plus
// the AWS Rekognition vector. Pure browser implementation, no extra SDK.

// 3-step enrollment matching the iOS app — front / left / right.
// Captures one photo per step on user click (not an auto-burst), so the user
// can hold each pose. Rekognition uses all 3 for a more robust enrollment.
const ENROLL_STEPS: { label: string; instruction: string; icon: string }[] = [
  { label: 'Front', instruction: 'Look straight at the camera', icon: '🙂' },
  { label: 'Left',  instruction: 'Turn your head slightly to the LEFT',  icon: '⬅️' },
  { label: 'Right', instruction: 'Turn your head slightly to the RIGHT', icon: '➡️' },
];

const FaceEnrollPanel: React.FC<{ me: Me; properties: PropertyLink[]; onEnrolled: () => void }> = ({ me, properties, onEnrolled }) => {
  const targetPropertyIds = (properties && properties.length > 0)
    ? properties.map((p) => p.property_id)
    : (me.property_id ? [me.property_id] : []);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<'idle' | 'preview' | 'uploading' | 'done' | 'error'>('idle');
  const [step, setStep] = useState(0);                  // 0=front, 1=left, 2=right
  const [frames, setFrames] = useState<string[]>([]);   // captured base64 frames
  const [thumbs, setThumbs] = useState<string[]>([]);   // data URLs for preview thumbs
  const [error, setError] = useState<string>('');
  const [capturing, setCapturing] = useState(false);    // brief lock while we grab the frame

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  const startCamera = async () => {
    setError('');
    setStep(0);
    setFrames([]);
    setThumbs([]);
    setPhase('preview');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e: any) {
      setError(e?.message || 'Could not access camera');
      setPhase('error');
    }
  };

  const grabFrame = (): { b64: string; dataUrl: string } | null => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const size = Math.min(v.videoWidth, v.videoHeight);
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, 720, 720);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const b64 = dataUrl.split(',')[1] || '';
    if (!b64) return null;
    return { b64, dataUrl };
  };

  const uploadFrames = async (allFrames: string[]) => {
    setPhase('uploading');
    try {
      if (targetPropertyIds.length === 0) {
        setError('No properties available for enrollment');
        setPhase('error');
        return;
      }
      let anyOk = false;
      let lastErr = '';
      for (const pid of targetPropertyIds) {
        const res = await api('/face/enroll', {
          method: 'POST',
          body: JSON.stringify({
            person_id: me.id,
            person_type: 'tenant',
            property_id: pid,
            images: allFrames,
            backend: 'aws_rekognition',
          }),
        });
        if (res?.error) {
          lastErr = res.error;
        } else {
          anyOk = true;
        }
      }
      if (!anyOk) {
        setError(lastErr || 'Enrollment failed');
        setPhase('error');
      } else {
        setPhase('done');
        stopStream();
        onEnrolled();
      }
    } catch (e: any) {
      setError(e?.message || 'Enrollment failed');
      setPhase('error');
    }
  };

  const capture = async () => {
    if (capturing) return;
    setCapturing(true);
    // Brief delay so the freeze flash overlay is visible.
    await new Promise((r) => setTimeout(r, 180));
    const f = grabFrame();
    if (!f) {
      setError('Failed to capture from the camera');
      setPhase('error');
      setCapturing(false);
      return;
    }
    const nextFrames = [...frames, f.b64];
    const nextThumbs = [...thumbs, f.dataUrl];
    setFrames(nextFrames);
    setThumbs(nextThumbs);
    setCapturing(false);
    if (step < ENROLL_STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      // All 3 photos captured — upload.
      await uploadFrames(nextFrames);
    }
  };

  const retake = () => {
    // Drop the last frame + step back one.
    if (frames.length === 0) return;
    setFrames((arr) => arr.slice(0, -1));
    setThumbs((arr) => arr.slice(0, -1));
    setStep((s) => Math.max(0, s - 1));
  };

  if (phase === 'idle') {
    return (
      <div>
        <p style={{ ...S.muted, marginBottom: 12 }}>
          We'll take 3 photos — front, then a slight turn left, then a slight turn right — to recognize you reliably at the door. Each photo is captured when you tap Capture.
        </p>
        <button onClick={startCamera} style={S.primaryBtn}>
          Start face enrollment
        </button>
      </div>
    );
  }
  if (phase === 'done') {
    return <p style={{ ...S.muted, color: '#10b981' }}>✓ Face enrolled successfully.</p>;
  }
  if (phase === 'error') {
    return (
      <div>
        <p style={{ ...S.muted, color: '#b91c1c' }}>{error || 'Something went wrong.'}</p>
        <button onClick={startCamera} style={S.secondaryBtn}>Try again</button>
      </div>
    );
  }

  const cur = ENROLL_STEPS[step];
  const photoCountLabel = `Photo ${step + 1} of ${ENROLL_STEPS.length}`;
  const isUploading = phase === 'uploading';

  return (
    <div>
      {/* Camera preview with a soft circular guide overlay */}
      <div style={{
        position: 'relative',
        margin: '12px auto',
        border: '2px solid #0f2440',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#000',
        aspectRatio: '1 / 1',
        maxWidth: 360,
      }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
        {/* Capture flash */}
        {capturing && (
          <div style={{ position: 'absolute', inset: 0, background: '#fff', opacity: 0.7, transition: 'opacity 0.15s' }} />
        )}
        {/* Face guide */}
        <div style={{
          position: 'absolute',
          inset: '12%',
          border: '3px dashed rgba(255,255,255,0.55)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }} />
      </div>

      {/* Step indicator + instruction */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 4,
        marginBottom: 8,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {photoCountLabel}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {ENROLL_STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                width: 8, height: 8, borderRadius: '50%',
                background: i < frames.length ? '#10b981' : i === step ? '#0f2440' : '#d4d4d8',
              }}
            />
          ))}
        </div>
      </div>

      <div style={{
        padding: 12,
        borderRadius: 10,
        background: '#f3f4f6',
        border: '1px solid #e5e7eb',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <span style={{ fontSize: 28, lineHeight: 1 }}>{cur.icon}</span>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{cur.instruction}</p>
          <p style={{ ...S.muted, fontSize: 12, marginTop: 2 }}>
            Good lighting, no hat or sunglasses, keep your face inside the dashed circle.
          </p>
        </div>
      </div>

      {/* Captured thumbnails — show what we have so far */}
      {thumbs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {ENROLL_STEPS.map((s, i) => (
            <div key={i} style={{
              flex: 1,
              aspectRatio: '1 / 1',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#f3f4f6',
              border: i < thumbs.length ? '2px solid #10b981' : '1px dashed #d4d4d8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {thumbs[i] ? (
                <img src={thumbs[i]} alt={s.label} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
              ) : (
                <span style={{ fontSize: 11, color: '#a1a1aa' }}>{s.label}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <button
        onClick={capture}
        disabled={capturing || isUploading}
        style={{ ...S.primaryBtn, opacity: (capturing || isUploading) ? 0.6 : 1 }}
      >
        {isUploading
          ? 'Uploading…'
          : capturing
            ? 'Capturing…'
            : step === ENROLL_STEPS.length - 1
              ? 'Capture & finish'
              : `Capture ${cur.label.toLowerCase()} photo`}
      </button>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {frames.length > 0 && !isUploading && (
          <button
            onClick={retake}
            disabled={capturing}
            style={{ ...S.secondaryBtn, marginTop: 0, flex: 1 }}
          >
            Retake last
          </button>
        )}
        <button
          onClick={() => { stopStream(); setPhase('idle'); }}
          disabled={isUploading || capturing}
          style={{ ...S.linkBtn, marginTop: 0, flex: 1 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ───── Inline styles ──────────────────────────────────────────────────────
// Plain inline styles to avoid Tailwind config edits / bundle drift.

const S: Record<string, React.CSSProperties> = {
  loginWrap: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#101010', padding: 16 },
  loginCard: { width: '100%', maxWidth: 400, backgroundColor: '#fff', padding: 28, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.45)' },
  logo: { fontSize: 28, fontWeight: 700, color: '#0f2440', margin: 0, letterSpacing: -0.5 },
  loginLogo: { display: 'block', height: 48, width: 'auto', objectFit: 'contain', margin: '0 0 4px' },
  dashLogo: { display: 'block', height: 32, width: 'auto', objectFit: 'contain' },
  // Big brand block — matches the login screen on app.intrlock.io: large
  // banner logo with the "cloud-based intercom" tagline tucked beneath the
  // wordmark, sitting on the black page background ABOVE the white card.
  brandBlock: { textAlign: 'center', marginBottom: 24, width: '100%', maxWidth: 400 },
  brandLogoLarge: { display: 'block', height: 180, width: 'auto', maxWidth: '100%', objectFit: 'contain', margin: '0 auto' },
  brandTagline: { color: '#ffffff', marginTop: -56, fontSize: 12, fontWeight: 500, textAlign: 'left', paddingLeft: '41%', lineHeight: 1, marginBottom: 0 },
  dashPage: { minHeight: '100vh', backgroundColor: '#101010', color: '#f4f4f5' },
  dashLogoLarge: { display: 'block', height: 56, width: 'auto', objectFit: 'contain' },
  dashTagline: { color: '#ffffff', marginTop: -16, fontSize: 9, fontWeight: 500, textAlign: 'left', paddingLeft: '41%', lineHeight: 1, marginBottom: 0 },
  dashPill: { color: '#a1a1aa', fontSize: 12, padding: '2px 8px', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999 },
  tabNav: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    gap: 4,
    padding: '8px 0',
    marginBottom: 12,
    overflowX: 'auto',
    background: 'rgba(16,16,16,0.95)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  tabLink: {
    flexShrink: 0,
    minHeight: 44,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: '#d4d4d8',
    textDecoration: 'none',
    borderRadius: 8,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid transparent',
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  },
  tabLinkActive: {
    background: 'rgba(16, 185, 129, 0.18)',
    color: '#ffffff',
    borderColor: 'rgba(16, 185, 129, 0.4)',
    fontWeight: 600,
  },
  h2: { fontSize: 16, fontWeight: 600, color: '#1f2937', margin: '6px 0 16px' },
  h3: { fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 8px' },
  muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.4 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box', marginTop: 10 },
  primaryBtn: { width: '100%', marginTop: 12, padding: '12px 16px', borderRadius: 10, border: 'none', backgroundColor: '#0f2440', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { width: '100%', marginTop: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#111827', fontSize: 15, fontWeight: 500, cursor: 'pointer' },
  dangerBtn: { width: '100%', marginTop: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid #fca5a5', backgroundColor: '#fff', color: '#b91c1c', fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  linkBtn: { width: '100%', minHeight: 44, marginTop: 10, padding: '8px 12px', border: 'none', background: 'transparent', color: '#3b82f6', fontSize: 13, cursor: 'pointer' },
  signOutBtn: { minHeight: 44, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' },
  err: { marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: '#fee2e2', color: '#991b1b', fontSize: 13 },
  dashWrap: { maxWidth: 640, margin: '0 auto', padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif', color: '#f4f4f5' },
  dashHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 4px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 16 },
  section: { backgroundColor: '#fff', color: '#111827', borderRadius: 12, padding: 18, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,.05)', border: '1px solid #f3f4f6' },
  fieldRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, borderBottom: '1px solid #f3f4f6' },
  fieldLabel: { color: '#6b7280' },
  successCard: { marginTop: 16, padding: 14, borderRadius: 10, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' },
  pinDisplay: { fontFamily: 'monospace', fontSize: 32, fontWeight: 700, letterSpacing: 4, color: '#065f46', margin: '8px 0' },
  callRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' },
};
